import { StrictMode } from "react";
import type { ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router";
import { Provider } from "react-redux";
import { AuthProvider } from "react-oidc-context";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { LicenseInfo } from "@mui/x-license";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import { theme } from "./theme";
import { router } from "./router";
import { store } from "./app/store";
import { authEnabled, oidcConfig } from "./auth/authConfig";
import { AuthGate } from "./auth/AuthGate";
import { registerAuthInterceptor } from "./auth/apiAuth";

registerAuthInterceptor();

const muiXLicenseKey = import.meta.env.VITE_MUI_X_LICENSE_KEY;
if (muiXLicenseKey) LicenseInfo.setLicenseKey(muiXLicenseKey);

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root element #root not found");

const app: ReactNode = (
  <Provider store={store}>
    <ThemeProvider
      defaultMode="system"
      theme={theme}
    >
      <CssBaseline />
      <LocalizationProvider
        adapterLocale="en-AU"
        dateAdapter={AdapterLuxon}
      >
        <RouterProvider router={router} />
      </LocalizationProvider>
    </ThemeProvider>
  </Provider>
);

let tree: ReactNode = app;
if (authEnabled) {
  tree = (
    <AuthProvider {...oidcConfig}>
      <AuthGate>{app}</AuthGate>
    </AuthProvider>
  );
}

createRoot(rootEl).render(<StrictMode>{tree}</StrictMode>);
